import { useArticleSearch } from '@/hooks/useArticleSearch';
import { ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ArticleSearchResult } from '../../services/articleService';
import { ArticleSuggestionItem } from './ArticleSuggestionItem';

interface SmartArticleSearchInputProps {
  placeholder?: string;
  onArticleSelect: (article: ArticleSearchResult) => void;
  onCustomInput: (query: string) => void;
  onEnterPress?: () => void;
  onBlur?: () => void;
  onClick?: () => void;
  className?: string;
  inputClassName?: string;
  value?: string;
  readOnly?: boolean;
  disabled?: boolean;
}

export interface SmartArticleSearchInputRef {
  focus: () => void;
}

export const SmartArticleSearchInput = forwardRef<
  SmartArticleSearchInputRef,
  SmartArticleSearchInputProps
>(
  (
    {
      placeholder = 'Rechercher un article ou saisir un nom personnalisé...',
      onArticleSelect,
      onCustomInput,
      onEnterPress,
      onBlur,
      onClick,
      className = '',
      inputClassName = '',
      value = '',
      readOnly = false,
      disabled = false,
    },
    ref
  ) => {
    const [query, setQuery] = useState(value);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [hasUserTyped, setHasUserTyped] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Exposer la méthode focus au parent
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    // Utilisation du hook optimisé
    const { suggestions, isLoading, error, searchArticles, clearSuggestions } = useArticleSearch({
      limit: 5,
      debounceMs: 300,
    });

    // Gestion du changement de texte
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setQuery(inputValue);
      setHasUserTyped(true);

      if (inputValue.trim().length >= 2) {
        searchArticles(inputValue);
        setShowSuggestions(true);
      } else {
        clearSuggestions();
        setShowSuggestions(false);
      }

      setSelectedIndex(-1);

      // Notifier le parent du changement pour article personnalisé
      onCustomInput(inputValue);
    };

    // Gestion de la navigation au clavier
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowDown':
          if (showSuggestions && suggestions.length > 0) {
            e.preventDefault();
            setSelectedIndex(prev =>
              prev === -1 ? 0 : Math.min(prev + 1, suggestions.length - 1)
            );
          }
          break;

        case 'ArrowUp':
          if (showSuggestions && suggestions.length > 0) {
            e.preventDefault();
            setSelectedIndex(prev => (prev <= 0 ? -1 : prev - 1));
          }
          break;

        case 'Enter':
          e.preventDefault();

          // Cas 1: Si un article est sélectionné dans la liste, le choisir
          if (
            showSuggestions &&
            suggestions.length > 0 &&
            selectedIndex >= 0 &&
            selectedIndex < suggestions.length
          ) {
            handleArticleSelect(suggestions[selectedIndex]);
            return;
          }

          // Cas 2: Si pas de suggestions affichées ou liste vide, copier le texte et passer au suivant
          if (query.trim()) {
            onEnterPress?.();
          }
          break;

        case 'Escape':
          setShowSuggestions(false);
          setSelectedIndex(-1);
          inputRef.current?.blur();
          break;
      }
    };

    // Gestion de la sélection d'un article
    const handleArticleSelect = (article: ArticleSearchResult) => {
      setQuery(article.name);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      setHasUserTyped(false);
      onArticleSelect(article);
    };

    // Fermer les suggestions quand on clique ailleurs
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target as Node) &&
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
          setSelectedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Mettre à jour l'affichage des suggestions quand les résultats changent
    useEffect(() => {
      if (query.trim().length >= 2 && hasUserTyped) {
        // Toujours afficher la dropdown si on a tapé au moins 2 caractères
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }, [suggestions, query, isLoading, error, hasUserTyped]);

    // Synchroniser avec la valeur externe
    useEffect(() => {
      if (value !== query) {
        setQuery(value);
      }
    }, [value]);

    return (
      <div className={`relative ${className}`}>
        {/* Input de recherche */}
        <div className="relative">
          {/* Wrapper cliquable pour gérer le clic sur input disabled */}
          {disabled && (
            <div
              className="absolute inset-0 z-10 cursor-pointer"
              onClick={onClick}
              title="Cliquez pour réinitialiser"
            />
          )}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0 && hasUserTyped) {
                setShowSuggestions(true);
              }
            }}
            onBlur={onBlur}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            className={`w-full pl-10 pr-4 py-2 border-2 border-blue-200 rounded-sm bg-blue-50 focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all duration-200 uppercase text-sm ${disabled ? 'bg-gray-100 cursor-pointer' : ''} ${inputClassName}`}
          />
          {isLoading && query.trim().length >= 2 && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent"></div>
            </div>
          )}
        </div>

        {/* Liste des suggestions */}
        {showSuggestions && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-sm shadow-lg overflow-hidden"
          >
            {error && (
              <div className="p-3 text-red-600 text-sm flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {!error &&
              suggestions.length > 0 &&
              suggestions.map((article, index) => (
                <ArticleSuggestionItem
                  key={article.id}
                  article={article}
                  isSelected={index === selectedIndex}
                  onClick={() => handleArticleSelect(article)}
                />
              ))}
          </div>
        )}
      </div>
    );
  }
);

SmartArticleSearchInput.displayName = 'SmartArticleSearchInput';
